import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Wrench, Download, Upload, Copy, Bell } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '../UI/dropdown-menu';
import { strategiesAPI } from '../../services/api';

// Fields to strip when exporting
const EXCLUDED_FIELDS = [
  'id', 'status', 'created_at', 'updated_at', 'executor_enabled',
  'last_tick_at', 'tick_interval_seconds', 'error_message',
];

function buildExportPayload(strategy, { formData, selectedType } = {}) {
  if (formData && selectedType) {
    // Builder mode — export from form state
    return {
      _sauronExport: true,
      _version: 1,
      name: formData.name,
      type: selectedType.id,
      provider: formData.provider,
      trading_mode: formData.tradingMode,
      parameters: { ...formData.parameters, assetClass: formData.assetClass },
    };
  }
  // Manager / floor mode — export from strategy object
  const payload = {
    _sauronExport: true,
    _version: 1,
  };
  for (const [key, value] of Object.entries(strategy)) {
    if (!EXCLUDED_FIELDS.includes(key)) {
      payload[key] = value;
    }
  }
  // Ensure parameters is an object
  if (typeof payload.parameters === 'string') {
    payload.parameters = JSON.parse(payload.parameters);
  }
  return payload;
}

function downloadJson(obj, filename) {
  const blob = new Blob([JSON.stringify(obj, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function validateImport(data) {
  if (!data || typeof data !== 'object') return 'Invalid JSON';
  if (!data._sauronExport) return 'Not a Sauron strategy export';
  if (!data.type) return 'Missing strategy type';
  if (!data.name) return 'Missing strategy name';
  if (!data.parameters || typeof data.parameters !== 'object') return 'Missing or invalid parameters';
  return null;
}

const StrategyToolsDropdown = ({
  strategy,
  formData,
  selectedType,
  mode, // 'builder' | 'manager' | 'floor'
  onImport,
  onDuplicate,
  onNotifications,
  showLabel,
}) => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const hasStrategy = mode === 'builder' ? !!(formData && selectedType) : !!strategy;
  const showImport = mode === 'builder' || mode === 'manager';

  const handleExport = () => {
    if (!hasStrategy) return;
    const payload = buildExportPayload(strategy, { formData, selectedType });
    const safeName = (payload.name || 'strategy').replace(/[^a-zA-Z0-9_-]/g, '_');
    downloadJson(payload, `${safeName}.json`);
    toast.success('Strategy exported');
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const parsed = JSON.parse(event.target.result);
        const error = validateImport(parsed);
        if (error) {
          toast.error(`Invalid import: ${error}`);
          return;
        }
        onImport?.(parsed);
      } catch {
        toast.error('Failed to parse JSON file');
      }
    };
    reader.readAsText(file);
    // Reset so re-selecting same file triggers change
    e.target.value = '';
  };

  const handleDuplicate = async () => {
    if (!hasStrategy) return;
    try {
      const payload = buildExportPayload(strategy, { formData, selectedType });
      const createData = {
        name: `${payload.name} (Copy)`,
        type: payload.type,
        provider: payload.provider,
        tradingMode: payload.trading_mode || 'paper',
        parameters: payload.parameters,
        brokerConnectionId: payload.broker_connection_id || strategy?.broker_connection_id || null,
      };
      if (payload.investment_cap != null) createData.investmentCap = payload.investment_cap;
      const res = await strategiesAPI.create(createData);
      toast.success('Strategy duplicated');
      if (mode === 'builder') {
        navigate(`/strategies/builder/${res.data?.id || res.id}`);
      } else {
        onDuplicate?.(res.data || res);
      }
    } catch (err) {
      toast.error(`Failed to duplicate: ${err.message}`);
    }
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".json,application/json"
        className="hidden"
        onChange={handleFileSelect}
      />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={`inline-flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-white/[0.06] transition-colors ${
              showLabel ? 'gap-1.5 px-3 py-1.5 text-xs font-medium border border-border bg-card hover:border-foreground/20' : 'h-8 w-8'
            }`}
            title="Strategy Tools"
          >
            <Wrench className="h-3.5 w-3.5" />
            {showLabel && 'Tools'}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[180px]">
          <DropdownMenuItem onClick={handleExport} disabled={!hasStrategy}>
            <Download className="h-4 w-4 mr-2" />
            Export as JSON
          </DropdownMenuItem>
          {showImport && (
            <DropdownMenuItem onClick={() => fileInputRef.current?.click()}>
              <Upload className="h-4 w-4 mr-2" />
              Import from JSON
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleDuplicate} disabled={!hasStrategy}>
            <Copy className="h-4 w-4 mr-2" />
            Duplicate Strategy
          </DropdownMenuItem>
          {onNotifications && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onNotifications}>
                <Bell className="h-4 w-4 mr-2" />
                Notifications
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};

export default StrategyToolsDropdown;
